import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import useConstants from "../../hooks/useConstants";
import { getLocations, updateLocationProducts, updateLocationNotes, updateLocationAccess } from "../../api/locations";

const TABS = ["Overview", "Products", "Site Notes", "Site Access"];

export default function LocationDetail() {
	const { locationId } = useParams();
	const { constants } = useConstants();
	const [loading, setLoading] = useState(true);
	const [location, setLocation] = useState(null);
	const [tab, setTab] = useState("Overview");

	const load = async () => {
		setLoading(true);
		const res = await getLocations();
		if (res && !res.error) {
			setLocation((res.data || []).find((l) => l.id === locationId) || null);
		}
		setLoading(false);
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationId]);

	if (loading) return <Loader />;
	if (!location) return <div>Location not found.</div>;

	return (
		<div>
			<PageHeader
				title={location.locationName}
				subtitle={`${location.streetAddress}, ${location.city}, ${location.state} ${location.zip}`}
				actions={
					<Link to={`/locations/${locationId}/edit`} className="btn-outline">
						Edit Location
					</Link>
				}
			/>
			<ul className="nav nav-tabs mb-3">
				{TABS.map((t) => (
					<li className="nav-item" key={t}>
						<button className={`nav-link ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
							{t}
						</button>
					</li>
				))}
			</ul>

			{tab === "Overview" && (
				<div className="card p-4">
					<Row label="Type" value={location.locationType?.Title || location.locationType} />
					<Row label="Contact" value={location.locationContactPersonName} />
					<Row label="Phone" value={location.locationPhone} />
				</div>
			)}
			{tab === "Products" && <ProductsTab location={location} constants={constants} onSaved={load} />}
			{tab === "Site Notes" && <NotesTab location={location} onSaved={load} />}
			{tab === "Site Access" && <AccessTab location={location} onSaved={load} />}
		</div>
	);
}

function Row({ label, value }) {
	return (
		<div className="d-flex justify-content-between border-bottom py-2">
			<span className="text-muted">{label}</span>
			<span className="fw-semibold">{value || "-"}</span>
		</div>
	);
}

function ProductsTab({ location, constants, onSaved }) {
	const [selected, setSelected] = useState((location.locationProducts || []).map((p) => p.id ?? p));
	const [saving, setSaving] = useState(false);

	const toggle = (id) => {
		setSelected((s) => (s.includes(id) ? s.filter((v) => v !== id) : [...s, id]));
	};

	const save = async () => {
		setSaving(true);
		const res = await updateLocationProducts(location.id, { locationProducts: selected });
		setSaving(false);
		if (res && !res.error) {
			toast.success("Products updated");
			onSaved();
		} else {
			toast.error(res?.msg || "Could not update products");
		}
	};

	return (
		<div className="card p-4">
			<div className="mb-3 text-muted small">Select the fuel products this location can receive.</div>
			<div className="d-flex flex-wrap gap-2 mb-4">
				{constants.locationProducts.map((opt) => (
					<button
						key={opt.id}
						type="button"
						className={`btn btn-sm ${selected.includes(opt.id) ? "btn-dark" : "btn-outline-secondary"}`}
						onClick={() => toggle(opt.id)}
					>
						{opt.Title}
					</button>
				))}
			</div>
			<button className="btn-accent-solid" style={{ width: 180 }} disabled={saving} onClick={save}>
				{saving ? "Saving..." : "Save Products"}
			</button>
		</div>
	);
}

function NotesTab({ location, onSaved }) {
	const [note, setNote] = useState(location.locationNotes || "");
	const [saving, setSaving] = useState(false);

	const save = async () => {
		setSaving(true);
		const res = await updateLocationNotes(location.id, { locationNotes: note });
		setSaving(false);
		if (res && !res.error) {
			toast.success("Notes saved");
			onSaved();
		} else {
			toast.error(res?.msg || "Could not save notes");
		}
	};

	return (
		<div className="card p-4">
			<label className="form-label">Truck entry, tank fill tube, tank locks, and other site notes</label>
			<textarea className="form-control mb-3" rows={6} value={note} onChange={(e) => setNote(e.target.value)} />
			<button className="btn-accent-solid" style={{ width: 160 }} disabled={saving} onClick={save}>
				{saving ? "Saving..." : "Save Notes"}
			</button>
		</div>
	);
}

function AccessTab({ location, onSaved }) {
	const entry = location.accessLocationForFuelTruckEntry || {};
	const [lat, setLat] = useState(entry.lat != null ? String(entry.lat) : "");
	const [long, setLong] = useState(entry.long != null ? String(entry.long) : "");
	const [saving, setSaving] = useState(false);

	const save = async () => {
		if (!lat.trim() || !long.trim() || isNaN(Number(lat)) || isNaN(Number(long))) {
			toast.error("Enter a valid latitude and longitude");
			return;
		}
		setSaving(true);
		const res = await updateLocationAccess(location.id, { type: "TruckEntry", location: { lat: lat.trim(), long: long.trim() } });
		setSaving(false);
		if (res && !res.error) {
			toast.success("Truck entry point saved");
			onSaved();
		} else {
			toast.error(res?.msg || "Could not save truck entry point");
		}
	};

	return (
		<div className="card p-4">
			<div className="text-muted small mb-3">Where fuel trucks enter your site. Drivers navigate to this point.</div>
			<div className="row g-3 mb-3">
				<div className="col-md-6">
					<label className="form-label">Latitude</label>
					<input className="form-control" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="e.g. 47.6101" />
				</div>
				<div className="col-md-6">
					<label className="form-label">Longitude</label>
					<input className="form-control" value={long} onChange={(e) => setLong(e.target.value)} placeholder="e.g. -122.2015" />
				</div>
			</div>
			<button className="btn-accent-solid" style={{ width: 160 }} disabled={saving} onClick={save}>
				{saving ? "Saving..." : "Save Access"}
			</button>
		</div>
	);
}
